/**
 * Tabs — shadcn-style wrapper around Radix Tabs.
 *
 * Used for the underlined-tab pattern across V1 list pages
 * (customers/invoices/bills). The active tab carries the brand spec's
 * 2px ink underline; inactive tabs are muted and turn `text-ink-2` on hover.
 *
 * For URL-driven tabs (where a tab corresponds to a search-param value), the
 * route owns the state and feeds it into `<TabsRoot value=… onValueChange=…>`.
 * Radix is happy with controlled-mode Tabs — no `defaultValue` needed.
 */
import * as React from "react";
import * as Tabs from "@radix-ui/react-tabs";
import { cn } from "../lib/cn.js";

export const TabsRoot = Tabs.Root;
export const TabsContent = Tabs.Content;

export const TabsList = React.forwardRef<
  React.ElementRef<typeof Tabs.List>,
  React.ComponentPropsWithoutRef<typeof Tabs.List>
>(function TabsList({ className, ...props }, ref) {
  return (
    <Tabs.List
      ref={ref}
      className={cn("flex gap-1 border-b border-line overflow-x-auto -mx-1 px-1", className)}
      {...props}
    />
  );
});

export interface TabsTriggerProps extends React.ComponentPropsWithoutRef<typeof Tabs.Trigger> {
  /** Optional count badge displayed after the label. */
  count?: number | undefined;
  /** Tone of the count badge. Defaults to muted; switch to `warn` for "with overdue" tabs. */
  countTone?: "default" | "warn";
}

export const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof Tabs.Trigger>,
  TabsTriggerProps
>(function TabsTrigger({ className, children, count, countTone = "default", ...props }, ref) {
  return (
    <Tabs.Trigger
      ref={ref}
      className={cn(
        "group inline-flex items-center gap-1.5 h-9 px-3.5 -mb-px text-sm border-b-2 border-transparent text-muted hover:text-ink-2 transition-colors whitespace-nowrap cursor-pointer disabled:cursor-not-allowed disabled:opacity-60",
        // Active = brand spec §13.2: sky underline + sky-700 text + bold.
        "data-[state=active]:border-accent data-[state=active]:text-accent-hover data-[state=active]:font-semibold",
        "focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2",
        className,
      )}
      {...props}
    >
      {children}
      {typeof count === "number" && count > 0 ? (
        <span
          className={cn(
            "inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-xs font-medium tabular-nums font-mono leading-none",
            // Default tone: muted bg-bg/text-muted — flips to sky-50/sky-700 when tab is active.
            countTone === "warn"
              ? "bg-bad-50 text-bad"
              : "bg-bg text-muted group-data-[state=active]:bg-accent-subtle group-data-[state=active]:text-accent-hover",
          )}
        >
          {count}
        </span>
      ) : null}
    </Tabs.Trigger>
  );
});
